import { useState, useEffect, useCallback } from "react";
import { CRITERIA_API, fetchWithAuth } from "../utils/api";

export function useCriteriaSettings() {
  const [criteriaList, setCriteriaList] = useState([]);
  const [isPercentageMode, setIsPercentageMode] = useState(true);
  const [scoringMode, setScoringMode] = useState('simple'); // 'criteria' or 'simple'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Calculate total points/percentages
  const totalPoints = criteriaList.reduce((sum, criterion) => 
    sum + (criterion.maxPoints || 0), 0
  );

  const fetchCriteria = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchWithAuth(CRITERIA_API);
      setCriteriaList(data.criteria || []);
      setIsPercentageMode(data.isPercentageMode ?? true);
      setScoringMode(data.scoringMode || 'simple');
      setHasChanges(false);
    } catch (err) {
      setError(err.message || "Failed to load criteria");
      console.error("Error loading criteria:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveCriteria = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      // Validate before saving
      if (scoringMode === 'criteria' && isPercentageMode && totalPoints !== 100) {
        throw new Error("Total percentage must equal 100%");
      }

      await fetchWithAuth(CRITERIA_API, {
        method: "PUT",
        body: JSON.stringify({
          criteria: criteriaList,
          isPercentageMode,
          scoringMode
        })
      });

      setHasChanges(false);
      setSuccessMessage("Criteria saved successfully!");
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
      return true;
    } catch (err) {
      setError(err.message || "Failed to save criteria");
      console.error("Error saving criteria:", err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [criteriaList, isPercentageMode, scoringMode, totalPoints]);

  const addCriterion = useCallback(() => {
    const newCriterion = {
      id: Date.now(),
      label: "",
      maxPoints: 0
    };
    setCriteriaList(prev => [...prev, newCriterion]);
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, []);

  const updateCriterion = useCallback((id, field, value) => {
    setCriteriaList(prev => prev.map(criterion =>
      criterion.id === id ? { ...criterion, [field]: value } : criterion
    ));
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, []);

  const removeCriterion = useCallback((id) => {
    setCriteriaList(prev => prev.filter(criterion => criterion.id !== id));
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, []);

  const reorderCriteria = useCallback((dragIndex, dropIndex) => {
    setCriteriaList(prev => {
      const newList = [...prev];
      const [removed] = newList.splice(dragIndex, 1);
      newList.splice(dropIndex, 0, removed);
      return newList;
    });
    setHasChanges(true);
  }, []);

  const togglePercentageMode = useCallback((usePercentage) => {
    if (usePercentage === isPercentageMode) return;

    // Convert existing criteria
    const convertedCriteria = criteriaList.map(criterion => ({
      ...criterion,
      maxPoints: usePercentage
        ? (criterion.maxPoints / 100) * 100 // Convert points to percentage
        : (criterion.maxPoints / 100) * 100 // Convert percentage to points
    }));

    setIsPercentageMode(usePercentage);
    setCriteriaList(convertedCriteria);
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, [criteriaList, isPercentageMode]);

  const toggleScoringMode = useCallback((mode) => {
    if (mode === scoringMode) return;
    
    setScoringMode(mode);
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, [scoringMode]);

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchCriteriaWithAbort = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchWithAuth(CRITERIA_API, { signal: abortController.signal });
        
        // Only update state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setCriteriaList(data.criteria || []);
          setIsPercentageMode(data.isPercentageMode ?? true);
          setScoringMode(data.scoringMode || 'simple');
        }
      } catch (err) {
        // Only set error if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setError(err.message || "Failed to load criteria");
          console.error("Error loading criteria:", err);
        }
      } finally {
        // Only update loading state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchCriteriaWithAbort();
    
    // Cleanup function to abort the request if component unmounts or effect re-runs
    return () => {
      abortController.abort();
    };
  }, []); // Remove fetchCriteria dependency to prevent unnecessary re-runs

  return {
    criteriaList,
    isPercentageMode,
    scoringMode,
    loading,
    error,
    hasChanges,
    totalPoints,
    successMessage,
    fetchCriteria,
    saveCriteria,
    addCriterion,
    updateCriterion,
    removeCriterion,
    reorderCriteria,
    togglePercentageMode,
    toggleScoringMode
  };
} 